import { useState, useEffect, useRef, useCallback } from 'react';
import { fetchApi } from '@/utils/api';
import { t } from '@/i18n';
import type { ChatConfig, ChatComment } from '@/types/dashboard';
import './ChatWidget.css';

interface ChatAuthor {
  uid: number;
  name: string;
  email?: string;
  avatar?: string;
}

interface CommentsResponse {
  comments: ChatComment[];
  nextCursor?: string;
  prevCursor?: string;
  hasMore: boolean;
  total: number;
  participants?: ChatAuthor[];
}

interface UsersSearchResponse {
  users: ChatAuthor[];
}

interface ChatWidgetProps {
  config: ChatConfig;
  contextKey: string;
  currentUser?: { uid: number; name: string; avatar?: string };
}

function getAvatarColor(name: string): string {
  const colors = [
    '#e53935', '#d81b60', '#8e24aa', '#5e35b1', '#3949ab',
    '#1e88e5', '#039be5', '#00897b', '#43a047', '#7cb342',
    '#c0ca33', '#fdd835', '#ffb300', '#fb8c00', '#f4511e',
    '#6d4c41', '#546e7a',
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % colors.length;
  return colors[index];
}

function getInitials(name: string): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function formatTime(timestamp: number): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diff = now.getTime() - date.getTime();

  if (diff < 60000) {
    return t('dashboard.chat.time.justNow');
  }
  if (diff < 3600000) {
    const mins = Math.floor(diff / 60000);
    return t('dashboard.chat.time.minutesAgo').replace('@count', String(mins));
  }
  if (diff < 86400000) {
    const hours = Math.floor(diff / 3600000);
    return t('dashboard.chat.time.hoursAgo').replace('@count', String(hours));
  }
  return date.toLocaleDateString();
}

function extractMentions(content: string): { name: string }[] {
  const matches = content.match(/@([\p{L}\p{N}._-]+)/gu) || [];
  const names = [...new Set(matches.map((m) => m.substring(1)))];
  return names.map((name) => ({ name }));
}

export function ChatWidget({ config, contextKey, currentUser }: ChatWidgetProps) {
  const compactLimit = config.compactLimit ?? 3;

  const [comments, setComments] = useState<ChatComment[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [postError, setPostError] = useState<string | null>(null);

  const [newContent, setNewContent] = useState('');
  const [replyContent, setReplyContent] = useState('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionUsers, setMentionUsers] = useState<ChatAuthor[]>([]);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionStartPos, setMentionStartPos] = useState(0);
  const [mentionIndex, setMentionIndex] = useState(0);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const replyTextareaRef = useRef<HTMLTextAreaElement>(null);
  const mentionDropdownRef = useRef<HTMLDivElement>(null);
  const mentionDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasOlder, setHasOlder] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const chatBodyRef = useRef<HTMLDivElement>(null);

  const loadComments = useCallback(async (direction?: 'before' | 'after', cursor?: string) => {
    if (loading) return;
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        context_key: contextKey,
        limit: '50',
      });
      if (cursor) {
        params.set('cursor', cursor);
        params.set('direction', direction || 'before');
      }

      const data = await fetchApi<CommentsResponse>(`/api/dashboard/comments?${params.toString()}`, {
        method: 'GET',
      });

      if (direction === 'before' && cursor) {
        setComments((prev) => [...data.comments].reverse().concat(prev));
      } else {
        setComments([...data.comments].reverse());
        requestAnimationFrame(() => {
          if (chatBodyRef.current) {
            chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
          }
        });
      }
      setTotal(data.total);
      setNextCursor(data.nextCursor ?? null);
      setHasOlder(data.hasMore);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(t('dashboard.chat.error.load'));
      console.error('Failed to load comments:', msg);
    } finally {
      setLoading(false);
    }
  }, [contextKey, compactLimit, loading]);

  const loadOlderMessages = useCallback(async () => {
    if (loadingOlder || !hasOlder || !nextCursor) return;
    setLoadingOlder(true);
    try {
      const el = chatBodyRef.current;
      const prevHeight = el ? el.scrollHeight : 0;
      const params = new URLSearchParams({
        context_key: contextKey,
        limit: '50',
        cursor: nextCursor,
        direction: 'before',
      });
      const data = await fetchApi<CommentsResponse>(
        `/api/dashboard/comments?${params.toString()}`, { method: 'GET' }
      );
      if (data.comments.length > 0) {
        const older = [...data.comments].reverse();
        setComments(prev => {
          const merged = [...older, ...prev];
          const seen = new Set<string>();
          return merged.filter(c => {
            if (seen.has(c.uuid)) return false;
            seen.add(c.uuid);
            return true;
          });
        });
      }
      setNextCursor(data.nextCursor ?? null);
      setHasOlder(data.hasMore);
      requestAnimationFrame(() => {
        if (el) {
          el.scrollTop = el.scrollHeight - prevHeight;
        }
      });
    } catch {
      // Silent — don't break UX
    } finally {
      setLoadingOlder(false);
    }
  }, [contextKey, nextCursor, hasOlder, loadingOlder]);

  useEffect(() => {
    const el = chatBodyRef.current;
    if (!el) return;
    const handleScroll = () => {
      if (el.scrollTop < 50 && !loadingOlder && hasOlder) {
        loadOlderMessages();
      }
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [loadingOlder, hasOlder, loadOlderMessages]);

  useEffect(() => {
    loadComments();
  }, [contextKey]);

  useEffect(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }
    pollingRef.current = setInterval(() => {
      const params = new URLSearchParams({
        context_key: contextKey,
        limit: '50',
      });
      if (comments.length > 0) {
        const newest = comments[comments.length - 1];
        const cursorVal = `${Math.floor(newest.created / 1000)}:${newest.uuid}`;
        params.set('cursor', cursorVal);
        params.set('direction', 'after');
      }
      fetchApi<CommentsResponse>(`/api/dashboard/comments?${params.toString()}`, { method: 'GET' })
        .then((data) => {
          if (data.comments.length > 0) {
            const newer = [...data.comments].reverse();
            setComments(prev => {
              const merged = [...prev, ...newer];
              const seen = new Set<string>();
              return merged.filter(c => {
                if (seen.has(c.uuid)) return false;
                seen.add(c.uuid);
                return true;
              });
            });
            setTotal(data.total);
          }
        })
        .catch(() => {
          // Polling errors are intentionally silent.
        });
    }, 30000);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [contextKey, comments]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        showMentions &&
        mentionDropdownRef.current &&
        !mentionDropdownRef.current.contains(e.target as Node)
      ) {
        setShowMentions(false);
      }
    }
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showMentions]);

  const autoResize = useCallback((el: HTMLTextAreaElement | null) => {
    if (!el) return;
    const minHeight = 48;
    const maxHeight = 312;
    el.style.height = 'auto';
    const newHeight = Math.min(maxHeight, Math.max(minHeight, el.scrollHeight));
    el.style.height = `${newHeight}px`;
    if (el.scrollHeight > maxHeight) {
      el.classList.add('chat-textarea--scrollable');
    } else {
      el.classList.remove('chat-textarea--scrollable');
    }
  }, []);

  const searchUsers = useCallback(async (query: string) => {
    try {
      const data = await fetchApi<UsersSearchResponse>(
        `/api/users/search?q=${encodeURIComponent(query)}&limit=10`,
        { method: 'GET' }
      );
      setMentionUsers(data.users || []);
    } catch {
      setMentionUsers([]);
    }
  }, []);

  const handleInputChange = useCallback((
    value: string,
    cursorPos: number,
    isReply: boolean
  ) => {
    if (isReply) {
      setReplyContent(value);
    } else {
      setNewContent(value);
    }

    const beforeCursor = value.substring(0, cursorPos);
    const atPos = beforeCursor.lastIndexOf('@');

    if (atPos >= 0 && atPos < cursorPos) {
      const query = beforeCursor.substring(atPos + 1);
      if (query.length >= 2 && !/\s/.test(query)) {
        setMentionQuery(query);
        setMentionStartPos(atPos);
        if (mentionDebounceRef.current) {
          clearTimeout(mentionDebounceRef.current);
        }
        mentionDebounceRef.current = setTimeout(() => {
          searchUsers(query);
        }, 200);
        setShowMentions(true);
        setMentionIndex(0);
        return;
      }
    }

    setShowMentions(false);
  }, [searchUsers]);

  const selectMention = useCallback((user: ChatAuthor, isReply: boolean) => {
    const target = isReply ? replyContent : newContent;
    const before = target.substring(0, mentionStartPos);
    const after = target.substring(mentionStartPos + mentionQuery.length + 1);
    const newValue = `${before}@${user.name} ${after}`;

    if (isReply) {
      setReplyContent(newValue);
      setTimeout(() => {
        if (replyTextareaRef.current) {
          replyTextareaRef.current.focus();
          autoResize(replyTextareaRef.current);
        }
      }, 0);
    } else {
      setNewContent(newValue);
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          autoResize(textareaRef.current);
        }
      }, 0);
    }
    setShowMentions(false);
  }, [mentionStartPos, mentionQuery, newContent, replyContent, autoResize]);

  const submitComment = useCallback(async (content: string, parentUuid?: string | null) => {
    const trimmed = content.trim();
    if (!trimmed || submitting) return;

    setSubmitting(true);
    setPostError(null);

    try {
      const response = await fetchApi<{ success: boolean; comment?: ChatComment; error?: string }>(
        '/api/dashboard/comments',
        {
          method: 'POST',
          body: JSON.stringify({
            context_key: contextKey,
            content: trimmed,
            parent_uuid: parentUuid || null,
            mentions: extractMentions(trimmed),
          }),
        }
      );

      if (response.success && response.comment) {
        if (parentUuid) {
          setComments((prev) =>
            prev.map((c) => {
              if (c.uuid === parentUuid) {
                const replies = c.replies ? [...c.replies, response.comment!] : [response.comment!];
                return { ...c, replies, repliesCount: (c.repliesCount || 0) + 1 };
              }
              return c;
            })
          );
          setReplyContent('');
          setReplyingTo(null);
        } else {
          setComments((prev) => [...prev, response.comment!]);
          setTotal((prev) => prev + 1);
          setNewContent('');
        }
        setTimeout(() => {
          if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.classList.remove('chat-textarea--scrollable');
          }
        }, 0);
      } else if (response.error) {
        setPostError(response.error);
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setPostError(t('dashboard.chat.error.send'));
      console.error('Failed to create comment:', msg);
    } finally {
      setSubmitting(false);
    }
  }, [contextKey, submitting]);

  const handleMainSubmit = useCallback(() => {
    submitComment(newContent);
  }, [newContent, submitComment]);

  const handleReplySubmit = useCallback((parentUuid: string) => {
    submitComment(replyContent, parentUuid);
  }, [replyContent, submitComment]);

  const handleMainKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (showMentions && mentionUsers.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionIndex((prev) => (prev + 1) % mentionUsers.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIndex((prev) => (prev - 1 + mentionUsers.length) % mentionUsers.length);
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        selectMention(mentionUsers[mentionIndex], false);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowMentions(false);
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleMainSubmit();
    }
  }, [handleMainSubmit, showMentions, mentionUsers, mentionIndex, selectMention]);

  const renderAvatar = (author: ChatAuthor, size: number) => {
    if (author.avatar) {
      return (
        <img
          src={author.avatar}
          alt={author.name}
          className="rounded-circle"
          width={size}
          height={size}
        />
      );
    }
    return (
      <div
        className="avatar-placeholder rounded-circle text-white d-flex align-items-center justify-content-center fw-semibold"
        style={{
          width: size,
          height: size,
          fontSize: size <= 24 ? 9 : 12,
          backgroundColor: getAvatarColor(author.name),
        }}
      >
        {getInitials(author.name)}
      </div>
    );
  };

  const renderMentionDropdown = (isReply: boolean) => (
    <div
      ref={mentionDropdownRef}
      className="mention-dropdown dropdown-menu show"
      style={{ display: showMentions ? 'block' : 'none' }}
    >
      {mentionUsers.length === 0 ? (
        <span className="dropdown-item text-muted">{t('dashboard.chat.noUsersFound')}</span>
      ) : (
        mentionUsers.map((user, index) => (
          <button
            key={user.uid}
            type="button"
            className={`dropdown-item d-flex align-items-center gap-2 ${index === mentionIndex ? 'mention-active' : ''}`}
            onClick={() => selectMention(user, isReply)}
            onMouseEnter={() => setMentionIndex(index)}
          >
            {user.avatar && (
              <img src={user.avatar} className="rounded-circle" width={24} height={24} alt="" />
            )}
            <span>{user.name}</span>
          </button>
        ))
      )}
    </div>
  );

  const renderComment = (comment: ChatComment, isReply = false) => {
    const isMine = comment.author.uid === currentUser?.uid;
    const wrapperClass = isMine
      ? 'chat-message chat-message--mine chat-pop'
      : 'chat-message chat-message--other chat-pop';

    return (
      <div
        key={comment.uuid}
        className={wrapperClass + (isReply ? '' : ' mb-2')}
        data-comment-uuid={comment.uuid}
      >
        {!isMine && (
          <div className="chat-message__avatar">
            {renderAvatar(comment.author, 32)}
          </div>
        )}
        <div className="chat-message__body">
          {!isMine && (
            <div className="chat-message__author">{comment.author.name}</div>
          )}
          <div className="chat-bubble">
            {comment.isDeleted ? (
              <div className="chat-message__content text-muted fst-italic">{t('dashboard.chat.deleted')}</div>
            ) : (
              <div
                className="chat-message__content"
                dangerouslySetInnerHTML={{ __html: comment.contentHtml || comment.content }}
              />
            )}
          </div>
          <span className="chat-message__time">{formatTime(comment.created)}</span>

          {!comment.isDeleted && !isReply && (
            <div className="mt-1">
              <button
                type="button"
                className="btn btn-link btn-sm p-0 text-secondary"
                onClick={() => {
                  setReplyingTo(comment.uuid);
                  setReplyContent('');
                }}
              >
                {t('dashboard.chat.reply')}
                {comment.repliesCount ? ` (${comment.repliesCount})` : ''}
              </button>
            </div>
          )}

          {replyingTo === comment.uuid && (
            <div className="mt-2">
              <div className="chat-input-wrapper">
                <div className="chat-input-container">
                  {renderMentionDropdown(true)}
                  <div className="chat-textarea-wrapper">
                    <button className="chat-tool-btn" aria-label="Attach file" title="Attach file">
                      <i className="la la-paperclip" />
                    </button>
                    <button className="chat-tool-btn" aria-label="Attach image" title="Attach image">
                      <i className="la la-image" />
                    </button>
                    <textarea
                      ref={replyTextareaRef}
                      className="chat-textarea"
                      placeholder={t('dashboard.chat.placeholder')}
                      value={replyContent}
                      onChange={(e) => {
                        handleInputChange(e.target.value, e.target.selectionStart, true);
                        autoResize(e.target);
                      }}
                      onKeyDown={(e) => {
                        if (showMentions && mentionUsers.length > 0) {
                          if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIndex((prev) => (prev + 1) % mentionUsers.length); return; }
                          if (e.key === 'ArrowUp') { e.preventDefault(); setMentionIndex((prev) => (prev - 1 + mentionUsers.length) % mentionUsers.length); return; }
                          if (e.key === 'Enter') { e.preventDefault(); selectMention(mentionUsers[mentionIndex], true); return; }
                          if (e.key === 'Escape') { e.preventDefault(); setShowMentions(false); return; }
                        }
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleReplySubmit(comment.uuid);
                        }
                        if (e.key === 'Escape') {
                          setReplyingTo(null);
                          setReplyContent('');
                        }
                      }}
                      disabled={submitting}
                      rows={1}
                    />
                    <button className="chat-tool-btn" aria-label="Emoji" title="Emoji">
                      <i className="la la-smile" />
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  className="chat-send-btn"
                  onClick={() => handleReplySubmit(comment.uuid)}
                  disabled={!replyContent.trim() || submitting}
                  title={t('dashboard.chat.send')}
                >
                  <i className={submitting ? 'las la-spinner la-spin' : 'las la-paper-plane'} />
                </button>
              </div>
            </div>
          )}

          {!isReply && comment.replies && comment.replies.length > 0 && (
            <div className="ms-4 mt-2 border-start ps-3">
              {comment.replies.map((reply) => renderComment(reply, true))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="chat-widget" data-testid="chat-widget">
      <div className="chat-header d-flex justify-content-between align-items-center p-3 border-bottom">
        <div className="d-flex align-items-center gap-2">
          <i className="las la-comments fs-4 text-primary" />
          <h6 className="mb-0">
            {config.title || t('dashboard.chat.title')} ({total})
          </h6>
        </div>
      </div>

      <div className="chat-body" ref={chatBodyRef}>
        {loadingOlder && (
          <div className="text-center py-2">
            <i className="las la-spinner la-spin text-muted" />
          </div>
        )}
        {error && (
          <div className="alert alert-danger m-3" role="alert">
            {error}
          </div>
        )}

        {loading && comments.length === 0 && (
          <div className="d-flex align-items-center justify-content-center py-4">
            <div className="text-center text-muted">
              <i className="las la-spinner la-spin la-2x mb-2 d-block" />
              <p className="mb-0 small">{t('dashboard.chat.loading')}</p>
            </div>
          </div>
        )}

        {!loading && comments.length === 0 && !error && (
          <div className="text-center text-muted py-4">
            <i className="las la-comment-slash fs-1" />
            <p className="mb-0 mt-2">{t('dashboard.chat.noComments')}</p>
          </div>
        )}

        <div className="chat-messages">
          {comments.map((comment) => renderComment(comment))}
        </div>
      </div>

      {currentUser && (
        <div className="chat-footer">
          {postError && (
            <div className="alert alert-danger alert-dismissible py-2 mb-2" role="alert">
              <span className="small">{postError}</span>
              <button
                type="button"
                className="btn-close btn-sm"
                onClick={() => setPostError(null)}
                aria-label="Close"
              />
            </div>
          )}
          <div className="chat-input-wrapper">
            <div className="chat-input-container">
              {showMentions && renderMentionDropdown(false)}
              <button className="chat-tool-btn" aria-label="Attach file" title="Attach file">
                <i className="la la-paperclip" />
              </button>
              <button className="chat-tool-btn" aria-label="Attach image" title="Attach image">
                <i className="la la-image" />
              </button>
              <textarea
                ref={textareaRef}
                className="chat-textarea"
                placeholder="Message..."
                value={newContent}
                onChange={(e) => {
                  handleInputChange(e.target.value, e.target.selectionStart, false);
                  autoResize(e.target);
                }}
                onKeyDown={handleMainKeyDown}
                disabled={submitting}
                rows={1}
              />
              <button className="chat-tool-btn" aria-label="Emoji" title="Emoji">
                <i className="la la-smile" />
              </button>
              <button
                className="chat-send-btn"
                onClick={handleMainSubmit}
                disabled={!newContent.trim() || submitting}
                aria-label={t('dashboard.chat.send')}
              >
                <i className={submitting ? 'las la-spinner la-spin' : 'las la-paper-plane'} />
              </button>
            </div>
          </div>
        </div>
      )}

      {!currentUser && (
        <div className="chat-login-prompt">
          <i className="las la-user-circle la-2x mb-2 opacity-50" />
          <p className="mb-0">{t('dashboard.chat.loginToComment')}</p>
        </div>
      )}
    </div>
  );
}
